import { Injectable } from '@angular/core';
import { NavigationExtras, Router } from '@angular/router';

@Injectable({
	providedIn: 'root',
})
export class RouterService {
	constructor(private router: Router) {}

	/**
	 * Navigates to a route using the same signature as Angular's {@link Router.navigate}.
	 *
	 * If navigation fails due to a lazy-loaded chunk or module failing to load (e.g. a
	 * `ChunkLoadError` after a deployment), the service attempts a hard-refresh by setting
	 * `window.location.href` to the serialized target URL, which forces the browser to
	 * re-fetch all assets for that route.
	 *
	 * If serializing the URL itself throws (e.g. the route is unrecognized), the service
	 * falls back to a full page reload via `window.location.reload()`.
	 *
	 * @param commands - An array of URL fragments to navigate to, identical to the first
	 *   argument of {@link Router.navigate}.
	 * @param extras - Optional {@link NavigationExtras} passed directly to the underlying
	 *   {@link Router.navigate} call.
	 * @returns A promise that resolves when navigation completes (or when a hard-refresh
	 *   is initiated — in which case the promise may never settle).
	 */
	public async navigate(commands: readonly any[], extras?: NavigationExtras): Promise<void> {
		try {
			await this.router.navigate(commands, extras);
		} catch (e) {
			console.error(e);
			try {
				const url = this.router.serializeUrl(this.router.createUrlTree(commands, extras));
				window.location.href = url;
			} catch (e2) {
				console.error(e2);
				window.location.reload();
			}
		}
	}
}
